import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, HeartHandshake, Lightbulb, ShieldCheck } from 'lucide-react';

import love from "../Photos/love1.png"
import idea from "../Photos/idea1.png"
import safe from "../Photos/safe.png"


// --- Framer Motion Variants for Staggered Animations ---
const headerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const sloganVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { delay: 0.8, duration: 0.7, ease: "easeOut" } },
};

const pointVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 120, damping: 15 } },
};

const OurMissionPage = () => {
    // --- Data for the Mission Page ---
    const missionPoints = [
        {
            icon: Lightbulb, // Keep Lucide icons for structure, but images will overlay/complement
            title: "Sparking Bright Ideas!",
            description: "We love to make learning super fun! We help kids discover amazing new things and ask exciting questions like 'Wow, what's that?!'",
            imageAlt: "A child with a glowing lightbulb above their head, surrounded by stars and question marks, in a cartoon style.",
            color: "text-amber-600 bg-amber-100",
        },
        {
            icon: HeartHandshake,
            title: "Happy Hearts & Friends!",
            description: "It's all about kindness and sharing! We help kids learn to be great friends, understand feelings, and share big smiles.",
            imageAlt: "Two diverse cartoon children holding hands and laughing, with hearts floating around them.",
            color: "text-rose-600 bg-rose-100",
        },
        {
            icon: ShieldCheck,
            title: "Super Safe Zone!",
            description: "This is a super safe place! No yucky ads, just awesome games and stories where grown-ups know their kids are safe.",
            imageAlt: "A friendly cartoon robot guardian standing in front of a colorful digital playground, with a shield icon.",
            color: "text-teal-600 bg-teal-100",
        },
    ];

    return (
        // Full-Screen Background and Main Wrapper (Edge-to-Edge)
        <motion.div
            className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-yellow-100 text-gray-800 font-sans w-full overflow-hidden" // More vibrant gradient
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
            <main className="relative z-10 pt-10 pb-20 w-full"> 
                
                {/* 1. Page Hero Section */}
                <header className="w-full pb-16 pt-8 px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        className="max-w-4xl mx-auto"
                        variants={headerVariants}
                    >
                        <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-pink-600 leading-tight drop-shadow-lg">
                            Our Super Fun Mission!
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-700 mt-4 max-w-2xl mx-auto leading-relaxed">
                            We believe every little explorer deserves a happy, safe, and exciting learning adventure!
                        </p>
                    </motion.div>
                </header>

                {/* 2. Main Content Area */}
                <section className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-12 border border-purple-200 backdrop-blur-sm bg-opacity-95"> {/* Softer border, slight blur */}
                        <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-12 text-center leading-tight">
                            The TalkyToon Promise to YOU!
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                            {missionPoints.map((point, index) => {
                                const PointIcon = point.icon; // Still include, maybe for a tiny badge or fallback
                                return (
                                    <motion.div
                                        key={index}
                                        className="text-center p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-100 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 group"
                                        variants={pointVariants}
                                    >
                                        {/* Image for each mission point */}
<div className="w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 mx-auto mb-6 relative">
                                            {/* Image 1: Fostering Curiosity */}
                                            {index === 0 && 
                                                <>
                                                <img 
                                                    src={idea}
                                                    alt={point.imageAlt} 
                                                    className="rounded-full object-cover w-full h-full border-4 border-amber-300 shadow-md group-hover:border-amber-400 transition-all"
                                                />
                                                <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-2 shadow-md border border-amber-200">
                                                  <PointIcon className="w-6 h-6 text-amber-500" />
                                                </div>
                                                </>
                                            }
                                            {/* Image 2: Prioritizing Well-being */}
                                            {index === 1 && 
                                                <>
                                                <img 
                                                    src={love}
                                                    alt={point.imageAlt} 
                                                    className="rounded-full object-cover w-full h-full border-4 border-rose-300 shadow-md group-hover:border-rose-400 transition-all"
                                                />
                                                <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-2 shadow-md border border-rose-200">
                                                  <PointIcon className="w-6 h-6 text-rose-500" />
                                                </div>
                                                </>
                                            }
                                            {/* Image 3: Ensuring Digital Safety */}
                                            {index === 2 && 
                                                <>
                                                <img 
                                                    src={safe}
                                                    alt={point.imageAlt} 
                                                    className="rounded-full object-cover w-full h-full border-4 border-teal-300 shadow-md group-hover:border-teal-400 transition-all"
                                                />
                                                <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-2 shadow-md border border-teal-200">
                                                  <PointIcon className="w-6 h-6 text-teal-500" />
                                                </div>
                                                </>
                                            }
                                        </div>
                                        
                                        <h3 className="text-2xl font-extrabold text-gray-900 mb-3">{point.title}</h3>
                                        <p className="text-lg text-gray-700 leading-relaxed">{point.description}</p>
                                    </motion.div>
                                );
                            })}
                        </div>

                        <motion.div
                            className="mt-16 pt-8 border-t border-gray-100 text-center"
                            variants={sloganVariants}
                        >
                            <p className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-blue-500 leading-tight">
                                "Where every tap is a leap into learning fun!"
                            </p>
                            <p className="text-xl text-gray-500 mt-3 font-medium">— The TalkyToon Team</p>
                        </motion.div>
                    </div>
                </section>
            </main>
        </motion.div>
    );
};

export default OurMissionPage;